import { useEffect, useRef, useState } from "react";
import {
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from "@heroicons/react/24/outline";
import * as SliderPrimitive from "@radix-ui/react-slider";

export interface VolumeControlProps {
  muted: boolean;
  volume: number;
  setMuted: (muted: boolean) => void;
  setVolume: (volume: number) => void;
}

/**
 * Speaker icon with a slide-over volume slider that reveals on hover (or
 * while actively dragging the slider). Clicking the icon toggles mute
 * independently of the hover reveal — the click never opens the slider.
 *
 * Close is debounced (200ms) so the slider doesn't snap shut if the
 * cursor briefly overshoots to the right after adjusting the volume.
 * `dragging` keeps it visible regardless while the user is actively
 * holding the thumb.
 */
export function VolumeControl({
  muted,
  volume,
  setMuted,
  setVolume,
}: VolumeControlProps) {
  const [hovering, setHovering] = useState(false);
  const [dragging, setDragging] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  const open = hovering || dragging;

  const handleEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setHovering(true);
  };
  const handleLeave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setHovering(false), 200);
  };

  return (
    <div
      className="flex items-center"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        onClick={() => setMuted(!muted)}
        className="flex h-6 w-6 items-center justify-center rounded-md text-[#282A30] transition-colors hover:bg-[#F7F8F9] hover:text-black"
        aria-label={muted ? "Unmute" : "Mute"}
      >
        {muted ? (
          <SpeakerXMarkIcon className="h-3.5 w-3.5" />
        ) : (
          <SpeakerWaveIcon className="h-3.5 w-3.5" />
        )}
      </button>
      <SliderPrimitive.Root
        min={0}
        max={1}
        step={0.01}
        value={[volume]}
        onValueChange={(vals) => setVolume(vals?.[0] ?? 0)}
        onPointerDown={() => setDragging(true)}
        onPointerUp={() => setDragging(false)}
        onPointerCancel={() => setDragging(false)}
        className={
          "relative flex h-5 touch-none select-none items-center overflow-hidden transition-[width,opacity,margin-left] duration-200 ease-out " +
          (open ? "ml-3 w-20 opacity-100" : "ml-0 w-0 opacity-0")
        }
      >
        <SliderPrimitive.Track className="relative h-1 grow overflow-hidden rounded-full bg-[#DFE1E4]">
          <SliderPrimitive.Range className="absolute h-full bg-[#282A30]" />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb className="block size-2 rounded-full bg-[#282A30] shadow-sm focus:outline-none" />
      </SliderPrimitive.Root>
    </div>
  );
}
